import useCartStore from '@/src/lib/state-stores/cartStore';
import { TCartItem } from '@/src/types/carts';
import { Link } from '@/src/i18n/navigation';
import { Minus, Plus } from 'lucide-react';
import React, { useTransition } from 'react';
import SlideUpAnimation from '@/src/components/animation/SlideUpAnimation';
import { Routes } from '@/src/routes/routes';
import Image from '@/src/components/Image';
import NextImage from 'next/image';
import { useTranslations } from 'next-intl';
import { generateSearchParams, IParams } from '@/src/lib/utils/createParams';
import Spinner from '@/src/components/ui/Spinner';
import DeleteImage from '@/public/trash.svg';
import SAR from '@/public/SAR';
import SaudiRealls, { TCountryId, TCurrency } from '@/src/components/sarComponent';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import CurrentLanguageText from '@/src/components/helper/CurrentLanguageText';
import PriceDisplay from '@/src/components/sarComponent';

type TProps = {
  cart: TCartItem;
  branchId: string;
  branchName: string;
  storeName: string;
  onCloseCartSheet?: () => void;
  disableUpdate?: boolean;
  cardType: number;
  currencyCode: TCurrency;
  countryId: TCountryId;
  storeId?: number;
};

const ProductCart = ({
  branchId,
  cart,
  onCloseCartSheet,
  disableUpdate = false,
  cardType,
  countryId,
  currencyCode,
  branchName,
  storeName,
  storeId,
}: TProps) => {
  const {
    defaultImage,
    itemArabicName,
    itemEnglishName,
    itemId,
    quantity,
    totalPrice,
    id,
    itemTypeId,
    isHaveValidDiscount,
    totalOriginalPrice,
    variantId,
    selectedVariantOptions,
    catItemStatus,
  } = cart;

  const ifProductHasVariant = Boolean(selectedVariantOptions?.length);

  const listOfProductVariants = selectedVariantOptions?.map((item) => ({
    key: item.varaintOptionEnglsihName.toLowerCase(),
    value: item.varaintOptionValueEnglishName,
  }));

  const params: IParams = {};
  listOfProductVariants?.forEach(({ key, value }) => {
    params[key] = value;
  });

  const isDigitalCard = cardType === 3;

  const productDetailsLink = isDigitalCard
    ? Routes.digitalCardDetails(itemId)
    : `${Routes.productDetails(branchId, storeName, itemId, itemEnglishName)}?${generateSearchParams(params)}`;

  const [isPending, startTransition] = useTransition();
  const { updateCart, removeFromCart, getCartIdByProductId } = useCartStore();
  const cartId = getCartIdByProductId(String(itemId));
  const t = useTranslations('cart');

  const handleAddCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(async () => {
      await updateCart({
        branchId,
        itemId,
        itemTypeId,
        variantId,
        quantity: quantity + 1,
      });
      trackEvent(AmplitudeEvents.product_quantity_increased, {
        product_id: itemId,
        productName: itemEnglishName,
        product_price: totalPrice,
        item_type_id: itemTypeId,
        cart_id: Number(cartId),
        variant_id: variantId,
        productQuantity: quantity,
        shop_id: Number(storeId),
        shop_name: itemEnglishName,
      });
    });
  };

  const handleRemoveCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(async () => {
      if (quantity > 1) {
        await updateCart({
          branchId,
          itemId,
          itemTypeId,
          variantId,
          quantity: quantity - 1,
        });
        trackEvent(AmplitudeEvents.product_quantity_decreased, {
          product_id: itemId,
          productName: itemEnglishName,
          product_price: totalPrice,
          item_type_id: itemTypeId,
          cart_id: Number(cartId),
          variant_id: variantId,
          productQuantity: quantity,
          shop_id: Number(storeId),
          shop_name: itemEnglishName,
        });
      } else {
        await removeFromCart(String(cartId), String(id));
        trackEvent(AmplitudeEvents.product_removed_from_cart, {
          product_id: itemId,
          productName: itemEnglishName,
          product_price: totalPrice,
          item_type_id: itemTypeId,
          cart_id: Number(cartId),
          variant_id: variantId,
          productQuantity: quantity,
          shop_id: Number(storeId),
          shop_name: itemEnglishName,
        });
      }
    });
  };

  const handleDelteCart = () => {
    startTransition(async () => {
      await removeFromCart(String(cartId), String(id));
    });
  };

  return (
    <div className="rounded-lg p-1 lg:py-3">
      <div className="flex justify-between">
        <div className="flex w-[70%] shrink-0 gap-3 lg:w-[75%]">
          <Link href={productDetailsLink} className="shrink-0" onClick={onCloseCartSheet}>
            <Image
              key={defaultImage}
              src={defaultImage ?? ''}
              width={120}
              local={!!isDigitalCard}
              height={120}
              alt="logo"
              className="h-14 w-14 rounded-lg object-cover shadow-sm lg:h-20 lg:w-20"
            />
          </Link>

          <div className="flex max-w-[75%] shrink-0 flex-col gap-2">
            <h3 className="me-2 line-clamp-1 text-sm font-bold lg:text-base">
              <CurrentLanguageText arabicName={itemArabicName} englishName={itemEnglishName} />
            </h3>

            {ifProductHasVariant && (
              <div className="lg:tex-sm text-gray-5 flex flex-wrap items-center gap-1 text-xs md:gap-2">
                {selectedVariantOptions?.map((item, index) => (
                  <div key={index} className="flex items-center">
                    <p>
                      <CurrentLanguageText
                        arabicName={item.varaintOptionArabicName}
                        englishName={item.varaintOptionEnglsihName}
                      />
                    </p>
                    <span className="bg-gray-5 mx-1 h-1 w-1 rounded-full" />
                    <p>
                      <CurrentLanguageText
                        arabicName={item.varaintOptionValueArabicName}
                        englishName={item.varaintOptionValueEnglishName}
                      />
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* update the cart  */}

            {!disableUpdate && (
              <>
                {' '}
                {catItemStatus.id !== 1 ? (
                  <button className="flex items-center gap-1" onClick={handleDelteCart}>
                    {isPending ? (
                      <Spinner size="small" />
                    ) : (
                      <NextImage
                        src={DeleteImage}
                        alt="trash"
                        width={15}
                        height={20}
                        className="h-4 w-4"
                      />
                    )}

                    <span className="text-xs font-bold text-red-500">{t('out-of-stock')}</span>
                  </button>
                ) : (
                  !isDigitalCard && (
                    <div
                      className={`${isPending ? 'pointer-events-none opacity-45' : ''} bg-primary-50/50 flex max-w-min items-center gap-4 rounded-full p-1`}
                    >
                      <button
                        onClick={handleAddCart}
                        className={`${quantity === 10 ? 'text-gray-5 pointer-events-none' : ''} flex h-8 w-8 cursor-pointer items-center justify-center`}
                      >
                        <Plus size={22} strokeWidth={2} />
                      </button>

                      <SlideUpAnimation uniqueKey={quantity}>{quantity}</SlideUpAnimation>

                      <button
                        onClick={handleRemoveCart}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center"
                      >
                        {quantity > 1 ? (
                          <Minus size={22} strokeWidth={2} />
                        ) : (
                          <NextImage
                            src={DeleteImage}
                            alt="trash"
                            width={15}
                            height={20}
                            className="h-4 w-4"
                          />
                        )}
                      </button>
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
        <div className="lg text-gray flex flex-col font-semibold">
          <div className="flex flex-row items-center gap-1.5 text-sm" dir="ltr">
            <PriceDisplay
              price={Number(totalPrice?.toFixed(2))}
              size="extraSmall"
              animation
              currency={currencyCode}
              countryId={countryId}
            />
          </div>
          {isHaveValidDiscount && (
            <span className="mx-2 text-gray-400 line-through">
              <PriceDisplay
                price={Number(totalOriginalPrice?.toFixed(2))}
                size="small"
                isHaveDiscount={true}
                currency={currencyCode}
                countryId={countryId}
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCart;
